// ЕДИНСТВЕННОЕ место арта игрового мира. Сервер шлёт ключи (роль игрока, вид сундука, иконка раздела), не адреса
// картинок (§2.18). Значки — объёмные Microsoft Fluent Emoji 3D (MIT, текст лицензии — public/licenses/), 128 px,
// по эмодзи-ключу; нет картинки — эмодзи на цветной плашке (у значка в строке — пустое место, HQ-2072). Придёт арт
// художника — меняем ICONS, экраны не трогаем.
import { useState } from 'react';
import styles from './World.module.css';

/** Fluent Emoji 3D (github.com/microsoft/fluentui-emoji, MIT): файл → адрес в сборке. */
const FILES = import.meta.glob<string>('../../assets/world/fluent/*.png', { eager: true, import: 'default' });
const file = (name: string): string | undefined => FILES[`../../assets/world/fluent/${name}.png`];
/** Эмодзи-заглушка → 3D-значок. */
const ICONS: Record<string, string> = {
  '🏏': 'cricket', '🎯': 'target', '⭐': 'star2', '🧤': 'gloves', '🏆': 'trophy', '🎁': 'gift', '💎': 'gem', '🏅': 'medal',
  '🌟': 'star', '⚔️': 'swords', '📋': 'clipboard', '🥉': 'bronze', '🥈': 'silver', '🥇': 'gold', '💠': 'diamond_dot',
  '👑': 'crown', '⛑️': 'helmet', '🦵': 'leg', '👟': 'shoe', '🔴': 'redball', '🧠': 'brain', '🏋️': 'lifting',
  '🧰': 'toolbox', '🎒': 'backpack', '🃏': 'joker', '👥': 'busts', '🛡️': 'shield', '🪙': 'coin', '⚡': 'energy',
  '🎉': 'party', '🛍️': 'shopping', '🔥': 'fire', '💰': 'moneybag', '✨': 'sparkles', '🔒': 'lock', '📦': 'package', '🏟️': 'stadium', '📜': 'scroll', '🎟️': 'ticket', '📖': 'book',
  '🎮': 'video_game', '👕': 'tshirt', '🗂️': 'dividers', '📅': 'calendar', '🧢': 'billed_cap', '⏱️': 'stopwatch', '💡': 'light_bulb', '💪': 'biceps', '🌪️': 'tornado', '🗺️': 'world_map', '👝': 'pouch',
};
/** Рисованные значки (Handpainted RPG Icons, LudicArts, CC-BY 3.0 — public/licenses/) — поверх Fluent, где есть. */
const PAINTED_FILES = import.meta.glob<string>('../../assets/world/painted/*.webp', { eager: true, import: 'default' });
const PAINTED: Record<string, string> = { '🎒': 'backpack', '📖': 'book', '📜': 'scroll', '🛡️': 'shield', '✨': 'crystal', '🧤': 'gauntlet', '⚔️': 'sword' };
/** Арт владельца (ChatGPT по «Промптам арта», 11.10) — поверх всего: src/assets/world/art/<имя файла из промпта>.webp. */
const ART_FILES = import.meta.glob<string>('../../assets/world/art/*.webp', { eager: true, import: 'default' });
export const artUrl = (name: string): string | undefined => ART_FILES[`../../assets/world/art/${name}.webp`];
/** Валюты шапки (C1–C3) и мешок добычи (B6) — по эмодзи-ключу. */
const OWNER_GLYPHS: Record<string, string> = { '💰': 'cur_gold', '⚔️': 'cur_power', '👊': 'cur_tap', '👝': 'btn_loot',
  // П2: валюты (C4–C7)
  '🪙': 'cur_coins', '💎': 'cur_gems', '🎟️': 'cur_tokens', '🏆': 'cur_cups',
  // П4: значки L1–L4 — замок, энергия (и сила удара), «в форме», подарок
  '🔒': 'icon_lock', '⚡': 'icon_energy', '🔥': 'icon_form', '🎁': 'icon_gift',
  // HQ-610 (21.10): медали, очки знатока, VIP, пропуск времени
  '🏅': 'cur_medals', '🎓': 'cur_expert', '👑': 'icon_vip', '⏩': 'icon_skip',
  // HQ-624 (21.10): карточка игрока, броня босса дня (🛡 без VS16 — щит клуба и гильдии 🛡️ не трогает), «как играть»
  '🃏': 'icon_card', '🛡': 'icon_boss_armor', '❓': 'icon_help',
  // HQ-2072 (30.10): свои значки вместо системных эмодзи — цена в Stars, места 1–3, лайв, секундомер, перчатки, звезда ступени
  '⭐': 'icon_stars', '🥇': 'place_1', '🥈': 'place_2', '🥉': 'place_3', '🔴': 'icon_live', '⏱': 'relic_stopwatch',
  '⏱️': 'relic_stopwatch', '🧤': 'item_gloves', '★': 'icon_star_on' };
/** HQ-2072: в строке (≤ SMALL_PX) — упрощённый рисунок: мелкие детали перчаток и медали в 16 px сливаются. */
const SMALL_PX = 24;
const OWNER_SMALL: Record<string, string> = { '🧤': 'icon_gloves_small', '🏅': 'icon_medal_small' };
/** Кнопки экрана боя (B1–B10) — по ключу иконки сервера (scene.buttons[].icon). */
const OWNER_SCENE: Record<string, string> = {
  // HQ-610 (21.10): вкладки «Режимов» «Матчи» и «Моя команда»
  matches: 'btn_matches', my_team: 'btn_my_team',
  heroes: 'btn_team', team: 'btn_team', shop: 'btn_shop', modes: 'btn_modes', chests: 'btn_chests', items: 'btn_kitbag',
  inventory: 'btn_kitbag', skills: 'btn_skills', map: 'btn_levels', guild: 'btn_guild', division: 'btn_division',
  // П2: папки «Команда» и «Режимы» (B11–B19)
  relics: 'btn_relics', achievements: 'btn_achievements', calendar: 'btn_calendar', daily_boss: 'btn_daily_boss', tasks: 'btn_tasks',
  camp: 'btn_camp', road: 'btn_road', event: 'btn_event', guild_boss: 'btn_guild_boss',
  // §285: «Неделя» (событие + гильдия + дивизион) — значок события
  week: 'btn_event',
  // §299: календарь входа (HQ-624 — свой значок)
  login: 'icon_login',
  // HQ-624 (21.10): клуб, друзья, чат болельщиков, альбом, «Команда недели», карточки
  club: 'btn_club', friends: 'btn_friends', community: 'btn_community', album: 'icon_album', totw: 'icon_totw', cards: 'icon_card',
  // §289: витрина — лавка (как кнопка «Магазин» сцены)
  store: 'btn_shop',
  // вкладки «Магазина» из готового арта: самоцветы — валюта, профиль силы — мощь; прежние названия кнопок
  gems: 'cur_gems', daily: 'btn_calendar', rewards: 'btn_calendar', today: 'btn_calendar', training: 'btn_camp',
  collection: 'btn_achievements',
  // П4: вкладки «Магазина» — форма (T1) и профиль силы (T2)
  look: 'btn_look', profile: 'btn_profile',
};
/** «art:<имя файла>» — прямо картинка владельца (навыки, реликвии): эмодзи для них нет. */
/** Арена боя по зоне сервера (battle.zone: street · school · club · state · national · world, смена каждые 50 уровней;
 *  арт владельца A1–A5); club и незнакомая зона — первая арена владельца (ground/arena.webp). */
export const arenaUrl = (zone: string | null | undefined, fallback: string): string => (zone && artUrl(`arena_${zone}`)) || fallback;
/** Картинки мира под заставку: весь арт владельца, кроме арен чужих зон (арена — одна, своей зоны), и писаные значки. */
export const preloadArtUrls = (): string[] => [
  ...Object.entries(ART_FILES).filter(([k]) => !/\/arena_[a-z]+\.webp$/.test(k)).map(([, url]) => url),
  ...Object.values(PAINTED_FILES),
];
/** Адрес значка; size — размер на экране: до SMALL_PX у части значков свой упрощённый рисунок. */
export const iconUrl = (glyph: string, size?: number): string | undefined =>
  (glyph.startsWith('art:') ? artUrl(glyph.slice(4)) : undefined)
  ?? (size != null && size <= SMALL_PX && OWNER_SMALL[glyph] ? artUrl(OWNER_SMALL[glyph]!) : undefined)
  ?? (OWNER_GLYPHS[glyph] ? artUrl(OWNER_GLYPHS[glyph]!) : undefined)
  ?? (PAINTED[glyph] ? PAINTED_FILES[`../../assets/world/painted/${PAINTED[glyph]}.webp`] : undefined) ?? (ICONS[glyph] ? file(ICONS[glyph]!) : undefined);

/** Пакет самоцветов по месту в списке витрины (сервер шлёт по возрастанию): первый — малый, последний — большой,
 *  между ними и единственный — средний. */
export function gemPackGlyph(index: number, count: number): string {
  const size = count > 1 && index === 0 ? 'small' : count > 1 && index === count - 1 ? 'large' : 'medium';
  return `art:gems_pack_${size}`;
}

/**
 * Поля сцены (Figma AI, src/assets/world/ground/README.md): ключ сервера (scene.ground.key, слот ground магазина) →
 * вертикальная картинка; нет вертикальной — широкий баннер того же стадиона с карты; незнакомый ключ — поле по умолчанию.
 */
const GROUNDS = import.meta.glob<string>('../../assets/world/ground/*.webp', { eager: true, import: 'default' });
const STADIUMS = import.meta.glob<string>('../../assets/world/stadium/*.webp', { eager: true, import: 'default' });
export const DEFAULT_GROUND = 'eden';
export const groundUrl = (key: string | null | undefined): string =>
  GROUNDS[`../../assets/world/ground/${key}.webp`] ?? STADIUMS[`../../assets/world/stadium/${key}.webp`]
    ?? GROUNDS[`../../assets/world/ground/${DEFAULT_GROUND}.webp`]!;

/** Значок в строке текста (монета, энергия): картинка размером со строку, по базовой линии. Нет картинки или не
 *  загрузилась (HQ-2072) — пустое место того же размера: строка не съезжает, системный эмодзи в текст не попадает.
 *  label — значение для экранного диктора, если рядом его не произносит ничто другое (иначе значок немой). */
export function Glyph({ glyph, size = 18, label }: { glyph: string; size?: number; label?: string }) {
  const url = iconUrl(glyph, size);
  const [broken, setBroken] = useState<string>();
  return url && url !== broken
    ? <img className={styles.glyph} src={url} alt={label ?? ''} width={size} height={size} aria-hidden={label ? undefined : true}
      onError={() => setBroken(url)} />
    : <span className={`${styles.glyph} ${styles.glyphGap}`} style={{ width: size, height: size }}
      role={label ? 'img' : undefined} aria-label={label} aria-hidden={label ? undefined : true} />;
}

interface Art { glyph: string; tone: string }

/** Вид сундука (chests.items[].kind). */
const CHESTS: Record<string, Art> = {
  match_won: { glyph: '🏆', tone: '#FFC700' },
  match_lost: { glyph: '🎁', tone: '#8FA3B8' },
  level: { glyph: '💎', tone: '#9B8CFF' },
  division: { glyph: '🏅', tone: '#4C9AFF' },
  season: { glyph: '🌟', tone: '#FFB020' },
  guild_war: { glyph: '⚔️', tone: '#FF8A65' },
  squad: { glyph: '📋', tone: '#4FC3A1' },
};
/** Ступень дивизиона (group.tier_key, tiers[].key); новые ступени без арта — нейтральная медаль. */
const TIERS: Record<string, Art> = {
  bronze: { glyph: '🥉', tone: '#D9A066' },
  silver: { glyph: '🥈', tone: '#C3CDD7' },
  gold: { glyph: '🥇', tone: '#FFC700' },
  platinum: { glyph: '💠', tone: '#8FD3E8' },
  diamond: { glyph: '💎', tone: '#6FB7FF' },
  legend: { glyph: '👑', tone: '#9B8CFF' },
};
/** Ячейка снаряжения (место вещи сумки боя: slot). */
const SLOTS: Record<string, string> = { bat: '🏏', gloves: '🧤', helmet: '⛑️', pads: '🦵', boots: '👟', ball: '🔴', cap: '🧢' };
/** Редкость вещи (gear[].rarity) — цвет плашки. */
const RARITY: Record<string, string> = { common: '#8FA3B8', rare: '#4C9AFF', epic: '#9B8CFF', legendary: '#FFB020' };
/** Круглые кнопки хаб-сцены (scene.buttons[].icon); новые кнопки сервера без арта — нейтральный кружок. */
const SCENE: Record<string, Art> = {
  tasks: { glyph: '📋', tone: '#4FC3A1' },
  // мир без нижней панели (12.10): «Матчи» — вкладкой «Режимов», «Клуб» — кнопкой сцены; арт «Матчей» — OWNER_SCENE
  matches: { glyph: '🏏', tone: '#4FC3A1' },
  club: { glyph: '🛡️', tone: '#4C9AFF' },
  my_team: { glyph: '🏏', tone: '#FFC700' },
  daily: { glyph: '🎁', tone: '#FF8A65' },
  training: { glyph: '🏋️', tone: '#4C9AFF' },
  chests: { glyph: '🧰', tone: '#FFC700' },
  inventory: { glyph: '🎒', tone: '#9B8CFF' },
  cards: { glyph: '🃏', tone: '#FFB020' },
  friends: { glyph: '👥', tone: '#8FD3E8' },
  shop: { glyph: '📦', tone: '#FFB020' },
  road: { glyph: '🏆', tone: '#FFB020' },
  stands: { glyph: '🏟️', tone: '#4FC3F7' },
  history: { glyph: '📜', tone: '#C8A06A' },
  album: { glyph: '📖', tone: '#B39DDB' },
  totw: { glyph: '🌟', tone: '#FFC700' },
  gems: { glyph: '💎', tone: '#4FC3F7' },
  // мир v3: окно «Команда»
  team: { glyph: '👥', tone: '#43C06B' },
  // папки сцены и вкладка «Сегодня» (вариант B, 11.10)
  rewards: { glyph: '🎁', tone: '#FF8A65' },
  games: { glyph: '🎮', tone: '#4C9AFF' },
  collection: { glyph: '🗂️', tone: '#FFB020' },
  look: { glyph: '👕', tone: '#9B8CFF' },
  today: { glyph: '📅', tone: '#4FC3A1' },
  // навигация мира v3 (§273): папки «Сегодня» и «Команда», «Карта» уровней и их вкладки
  map: { glyph: '🗺️', tone: '#4FC3A1' },
  calendar: { glyph: '📅', tone: '#4FC3A1' },
  daily_boss: { glyph: '👑', tone: '#FFC700' },
  items: { glyph: '🧤', tone: '#9B8CFF' },
  relics: { glyph: '✨', tone: '#FFB020' },
  camp: { glyph: '🏋️', tone: '#4C9AFF' },
  profile: { glyph: '💪', tone: '#FF8A65' },
  // раскладка по углам (§273-B, как у Dungeon Crusher): шлем — команда, коробка — магазин, сумка — режимы, книга — навыки
  heroes: { glyph: '👥', tone: '#43C06B' },
  modes: { glyph: '🎒', tone: '#9B8CFF' },
  skills: { glyph: '📖', tone: '#4C9AFF' },
  achievements: { glyph: '🏅', tone: '#FFC700' },
  guild: { glyph: '🛡️', tone: '#4C9AFF' },
  // §290: чат болельщиков (арт — OWNER_SCENE)
  community: { glyph: '💬', tone: '#4C9AFF' },
  division: { glyph: '🏅', tone: '#4C9AFF' },
  // этап 5 (§275): недельное событие и босс гильдии
  event: { glyph: '🎉', tone: '#FFB020' },
  guild_boss: { glyph: '⚔️', tone: '#E5484D' },
};
const NEUTRAL_CHEST: Art = { glyph: '🎁', tone: '#8FA3B8' };

/** Валюты и прочие значки мира: золото, кубки, самоцветы, медали, очки знатока (В4, HQ-782); монет нет нигде. */
/** Медали — с HQ-781 (§373) ими платит всё, что платило монеты. */
export const MEDAL = '🏅';
export const XP = 'XP';
export const ENERGY = '⚡';
/** Самоцвет — редкая валюта мира (только зарабатывается). */
export const GEM = '💎';
/** Telegram Stars — цена настоящими деньгами (значок icon_stars, HQ-2072). */
export const STARS = '⭐';

/** Контуры иконок разделов (tab_bar.tabs[].icon), 24×24 — в стиле TabIcon приложения. */
const TAB_PATHS: Record<string, string> = {
  player: 'M12 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8M4.5 20.5c.8-3.8 3.8-6 7.5-6s6.7 2.2 7.5 6z',
  matches: 'M3 5h18v14H3zM12 5v14M3 9h3v6H3M21 9h-3v6h3M12 10.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3',
  rating: 'M9 9h6v12H9zM3 14h6v7H3zM15 12h6v9h-6zM12 2.5l1.2 2.4 2.6.4-1.9 1.8.5 2.6-2.4-1.3-2.4 1.3.5-2.6-1.9-1.8 2.6-.4z',
  guild: 'M12 3l8 3v5c0 5-3.4 8.6-8 10-4.6-1.4-8-5-8-10V6zM12 8.5a2 2 0 1 0 0 4 2 2 0 0 0 0-4M8.5 16.5c.6-1.7 1.9-2.6 3.5-2.6s2.9.9 3.5 2.6',
  // навигация мира v3 (§273): «Бой» — калитка с перекладинами, «Клуб» — люди под флагом
  battle: 'M7 7h10M7 7v13M12 7v13M17 7v13M6 4.5h5M13 4.5h5',
  club: 'M9 10a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5M16 11a2 2 0 1 0 0-4 2 2 0 0 0 0 4M3.5 19c.6-3.2 2.8-5 5.5-5s4.9 1.8 5.5 5zM14.5 14.3c.5-.2 1-.3 1.5-.3 2.2 0 4 1.5 4.5 5h-5',
};

function Badge({ art, size, src }: { art: Art; size: number; src?: string }) {
  const url = src ?? iconUrl(art.glyph);
  // не загрузилась — эмодзи на плашке
  const [broken, setBroken] = useState<string>();
  // 3D-значок — сам по себе объёмный: без плашки, с мягкой тенью цвета ключа
  if (url && url !== broken) {
    return (
      <span className={styles.art3d} aria-hidden="true" style={{ width: size, height: size, ['--tone' as string]: art.tone }}>
        <img src={url} alt="" width={size} height={size} draggable={false} onError={() => setBroken(url)} />
      </span>
    );
  }
  return (
    <span className={styles.art} aria-hidden="true"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.52), ['--tone' as string]: art.tone }}>
      {art.glyph}
    </span>
  );
}

export function SceneButtonArt({ icon, size = 48 }: { icon: string; size?: number }) {
  return <Badge art={SCENE[icon] ?? { glyph: '•', tone: '#8FA3B8' }} size={size} src={OWNER_SCENE[icon] && artUrl(OWNER_SCENE[icon]!)} />;
}

/** Эмодзи-ключ сундука (для эффекта награды). */
export const chestGlyph = (kind: string): string => (CHESTS[kind] ?? NEUTRAL_CHEST).glyph;

/** П4 (K1–K3): сундук по ценности — деревянный (проигрыш, состав), серебряный (победа, дивизион, война гильдий),
 *  золотой (сезон, уровень); незнакомый вид — серебряный. */
const CHEST_ART: Record<string, string> = { match_lost: 'chest_wood', squad: 'chest_wood', match_won: 'chest_silver', division: 'chest_silver',
  guild_war: 'chest_silver', season: 'chest_gold', level: 'chest_gold' };

export function ChestArt({ kind, size = 48 }: { kind: string; size?: number }) {
  // пусто — эмодзи-заглушка
  const art = CHESTS[kind] ?? NEUTRAL_CHEST;
  return <Badge art={art} size={size} src={kind !== 'empty' ? artUrl(CHEST_ART[kind] ?? 'chest_silver') : undefined} />;
}

export function TierArt({ tier, size = 44 }: { tier: string | null | undefined; size?: number }) {
  // П4 (D1–D6): значок ступени владельца; новая ступень без арта — нейтральная медаль
  return <Badge art={(tier && TIERS[tier]) || { glyph: '🏅', tone: '#C3CDD7' }} size={size} src={tier ? artUrl(`tier_${tier}`) : undefined} />;
}

/** Гильдия: щит-заглушка (герба у гильдии пока нет). */
export function GuildArt({ size = 48 }: { size?: number }) {
  return <Badge art={{ glyph: '🛡️', tone: '#4C9AFF' }} size={size} src={artUrl('btn_guild')} />;
}

/** Вещь: значок слота на плашке цвета редкости; пустая ячейка (rarity нет) — серая и блёклая. */
export function GearArt({ slot, rarity, size = 44 }: { slot: string; rarity?: string | null; size?: number }) {
  const art = { glyph: SLOTS[slot] ?? '🎒', tone: (rarity && RARITY[rarity]) || '#C3CDD7' };
  // П2: вещи владельца (I1–I6) по месту, мяч — HQ-624; не загрузилась — эмодзи
  return <Badge art={art} size={size} src={artUrl(`item_${slot}`)} />;
}

/** Цвет редкости для подписи/рамки. */
export const rarityTone = (rarity?: string | null) => (rarity && RARITY[rarity]) || '#8FA3B8';

export function WorldTabIcon({ name, filled }: { name: string; filled: boolean }) {
  const d = TAB_PATHS[name];
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true"
      fill={filled ? 'currentColor' : 'none'} fillOpacity={filled ? 0.18 : 0}
      stroke="currentColor" strokeWidth={filled ? 2 : 1.6} strokeLinejoin="round" strokeLinecap="round">
      {d ? <path d={d} /> : <circle cx="12" cy="12" r="7" />}
    </svg>
  );
}
